import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { type Property, getSupabase } from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  display_name: z.string().trim().min(1).max(120),
  line1: z.string().trim().min(1).max(120),
  line2: z.string().trim().max(120).optional().or(z.literal("")),
  postcode: z
    .string()
    .trim()
    .regex(/^[A-Z0-9 ]{2,10}$/i, "Enter a UK postcode"),
  notes: z.string().trim().max(500).optional().or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

export function AdminProperties() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Property | "new" | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const supabase = getSupabase();
    const { data, error: err } = (await supabase
      .from("properties")
      .select("*")
      .order("display_name", { ascending: true })) as {
      data: Property[] | null;
      error: { message: string } | null;
    };
    if (err) setError(err.message);
    setProperties(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <LoadingPanel />;

  return (
    <div className="flex flex-col gap-6">
      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Properties</h2>
        <Button size="sm" onClick={() => setEditing(editing === "new" ? null : "new")}>
          <Plus size={14} />
          {editing === "new" ? "Cancel" : "Add property"}
        </Button>
      </div>
      {editing ? (
        <PropertyForm
          initial={editing === "new" ? null : editing}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}
      {properties.length === 0 ? (
        <p className="text-sm text-ink-dim">No properties yet.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {properties.map((p) => (
            <li key={p.id}>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="truncate">{p.display_name}</CardTitle>
                      <CardDescription className="truncate">
                        {[p.line1, p.line2, p.postcode].filter(Boolean).join(", ")}
                      </CardDescription>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                      <Pencil size={14} /> Edit
                    </Button>
                  </div>
                </CardHeader>
                {p.notes ? (
                  <CardContent>
                    <p className="text-xs text-ink-dim whitespace-pre-wrap">{p.notes}</p>
                  </CardContent>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PropertyForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial: Property | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initial
      ? {
          display_name: initial.display_name,
          line1: initial.line1,
          line2: initial.line2 ?? "",
          postcode: initial.postcode,
          notes: initial.notes ?? "",
        }
      : undefined,
  });
  const [globalError, setGlobalError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    const supabase = getSupabase();
    const payload = {
      display_name: values.display_name,
      line1: values.line1,
      line2: values.line2 || null,
      postcode: values.postcode.toUpperCase(),
      notes: values.notes || null,
    };
    const { error } = initial
      ? await supabase.from("properties").update(payload).eq("id", initial.id)
      : await supabase.from("properties").insert(payload);
    if (error) {
      setGlobalError(error.message);
      return;
    }
    onSaved();
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{initial ? "Edit property" : "New property"}</CardTitle>
        {initial ? (
          <CardDescription>
            Notes are visible to the agent at this property — useful for boiler model, stopcock
            location, parking instructions, etc.
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent>
        <form className="grid sm:grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field
            id="display_name"
            label="Display name"
            error={errors.display_name?.message}
            className="sm:col-span-2"
          >
            <Input id="display_name" placeholder="18 Siskin Grove" {...register("display_name")} />
          </Field>
          <Field id="line1" label="Address line 1" error={errors.line1?.message}>
            <Input id="line1" {...register("line1")} />
          </Field>
          <Field id="line2" label="Address line 2" error={errors.line2?.message}>
            <Input id="line2" {...register("line2")} />
          </Field>
          <Field id="postcode" label="Postcode" error={errors.postcode?.message}>
            <Input id="postcode" {...register("postcode")} />
          </Field>
          <Field id="notes" label="Notes" error={errors.notes?.message} className="sm:col-span-2">
            <textarea
              id="notes"
              rows={3}
              className="w-full rounded-xl border border-line bg-bg-card p-3 text-sm text-ink"
              placeholder="Boiler is in the loft. Stopcock under kitchen sink. Etc."
              {...register("notes")}
            />
          </Field>
          {globalError ? (
            <p role="alert" className="text-xs text-red-300 sm:col-span-2">
              {globalError}
            </p>
          ) : null}
          <div className="sm:col-span-2 flex gap-2">
            <Button type="submit" disabled={isSubmitting}>
              <Save size={14} />
              {isSubmitting ? "Saving…" : initial ? "Save changes" : "Save property"}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
